import {
  DEFAULT_SYMPHONY_GATEWAY,
  DEFAULT_SYMPHONY_LOADER,
} from "@/symphony/contract";

export interface SymphonyFlagInput {
  envEnabled?: string;
  allowedUserIds?: string;
  publishableKey?: string;
  userId?: string | null;
  appMetadata?: Record<string, unknown> | null;
  userMetadata?: Record<string, unknown> | null;
}

function truthyFlag(value: unknown): boolean {
  return value === true || value === "true";
}

export function isSymphonyWidgetEnabled(input: SymphonyFlagInput): boolean {
  if (input.envEnabled === "true") return true;
  if (truthyFlag(input.appMetadata?.symphony_widget)) return true;
  if (truthyFlag(input.userMetadata?.symphony_widget)) return true;
  const allowed = (input.allowedUserIds ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  return Boolean(input.userId && allowed.includes(input.userId));
}

export function isAllowedSymphonyLoader(loaderUrl: string, gateway: string): boolean {
  try {
    const loader = new URL(loaderUrl);
    const gate = new URL(gateway);
    if (loader.protocol !== "https:" || gate.protocol !== "https:") return false;
    const hosts = new Set(["symphony-vidara.vercel.app", gate.hostname]);
    return hosts.has(loader.hostname);
  } catch {
    return false;
  }
}

export interface SymphonyRuntimeConfig {
  enabled: boolean;
  publishableKey: string;
  loaderUrl: string;
  gateway: string;
  allowedUserIds: string;
}

export function readSymphonyRuntimeConfig(env: {
  VITE_SYMPHONY_ENABLED?: string;
  VITE_SYMPHONY_PUBLISHABLE_KEY?: string;
  VITE_SYMPHONY_LOADER_URL?: string;
  VITE_SYMPHONY_GATEWAY?: string;
  VITE_SYMPHONY_ALLOWED_USER_IDS?: string;
} = import.meta.env): SymphonyRuntimeConfig {
  const gateway = env.VITE_SYMPHONY_GATEWAY?.trim() || DEFAULT_SYMPHONY_GATEWAY;
  const loaderUrl = env.VITE_SYMPHONY_LOADER_URL?.trim() || DEFAULT_SYMPHONY_LOADER;
  return {
    enabled: env.VITE_SYMPHONY_ENABLED === "true",
    publishableKey: env.VITE_SYMPHONY_PUBLISHABLE_KEY?.trim() ?? "",
    loaderUrl,
    gateway,
    allowedUserIds: env.VITE_SYMPHONY_ALLOWED_USER_IDS ?? "",
  };
}

export function canMountSymphonyWidget(input: SymphonyFlagInput & { loaderUrl: string; gateway: string }): boolean {
  return Boolean(
    input.publishableKey
    && isSymphonyWidgetEnabled(input)
    && isAllowedSymphonyLoader(input.loaderUrl, input.gateway),
  );
}
